/**
 * Legacy subset used by older language pickers and fallback behavior.
 * Caregiver communication preferences accept the broader ISO table below;
 * voice capability comes from each organization's configured call routes.
 * Mirrors SUPPORTED_LANGUAGES in libraries/python/language/__init__.py
 */
export const SUPPORTED_LANGUAGES: Record<string, string> = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    ht: 'Haitian Creole',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
    ru: 'Russian',
    zh: 'Chinese',
    ja: 'Japanese',
    ko: 'Korean',
    vi: 'Vietnamese',
    hi: 'Hindi',
    pl: 'Polish',
    uk: 'Ukrainian',
    nl: 'Dutch',
    tr: 'Turkish',
    th: 'Thai',
    id: 'Indonesian',
    el: 'Greek',
    ro: 'Romanian',
    ms: 'Malay',
    bg: 'Bulgarian',
    ca: 'Catalan',
    cs: 'Czech',
    da: 'Danish',
    et: 'Estonian',
    fi: 'Finnish',
    hu: 'Hungarian',
    lv: 'Latvian',
    lt: 'Lithuanian',
    no: 'Norwegian',
    sk: 'Slovak',
    sv: 'Swedish',
} as const;

/**
 * Explicit speech-settings fallback for Haitian Creole. Its text preference
 * remains Haitian Creole, while speech providers receive French settings.
 * Mirrors VOICE_FALLBACK_LANGUAGES in libraries/python/language/__init__.py
 */
export const VOICE_FALLBACK_LANGUAGES: Record<string, string> = {
    ht: 'fr',
} as const;

/**
 * Languages with an explicit provider speech-settings fallback.
 */
export const VOICE_UNSUPPORTED_LANGUAGES: Set<string> = new Set<string>(
    Object.keys(VOICE_FALLBACK_LANGUAGES),
);

const LANGUAGE_NAME_TO_CODE: Record<string, string> = Object.fromEntries(
    Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => [
        name.toLowerCase(),
        code,
    ]),
);

/**
 * Normalize a language code or display name ("en-US", "English") to its ISO
 * 639-1 base code, or null when the language is not supported. Caregiver
 * language lists can hold either form (EHR syncs store display names).
 * Mirrors normalize_supported_language and _normalize_language_code_or_name
 * in libraries/python.
 */
export function normalizeSupportedLanguage(language: string): string | null {
    const normalized = language.trim().toLowerCase();
    if (!normalized) {
        return null;
    }

    const code = normalized.split('-', 1)[0];
    if (code !== undefined && code in SUPPORTED_LANGUAGES) {
        return code;
    }

    return LANGUAGE_NAME_TO_CODE[normalized] ?? null;
}

/**
 * Full ISO 639-1 code -> English name table. SMS/text conversations are not
 * limited to SUPPORTED_LANGUAGES. Voice support varies by organization and
 * its active call stacks; text paths resolve against this broad table.
 * Mirrors ISO_639_1_LANGUAGE_NAMES in libraries/python/language/__init__.py
 */
export const ISO_639_1_LANGUAGE_NAMES: Record<string, string> = {
    aa: 'Afar',
    ab: 'Abkhazian',
    ae: 'Avestan',
    af: 'Afrikaans',
    ak: 'Akan',
    am: 'Amharic',
    an: 'Aragonese',
    ar: 'Arabic',
    as: 'Assamese',
    av: 'Avaric',
    ay: 'Aymara',
    az: 'Azerbaijani',
    ba: 'Bashkir',
    be: 'Belarusian',
    bg: 'Bulgarian',
    bh: 'Bihari languages',
    bi: 'Bislama',
    bm: 'Bambara',
    bn: 'Bengali',
    bo: 'Tibetan',
    br: 'Breton',
    bs: 'Bosnian',
    ca: 'Catalan',
    ce: 'Chechen',
    ch: 'Chamorro',
    co: 'Corsican',
    cr: 'Cree',
    cs: 'Czech',
    cu: 'Church Slavic',
    cv: 'Chuvash',
    cy: 'Welsh',
    da: 'Danish',
    de: 'German',
    dv: 'Divehi',
    dz: 'Dzongkha',
    ee: 'Ewe',
    el: 'Greek',
    en: 'English',
    eo: 'Esperanto',
    es: 'Spanish',
    et: 'Estonian',
    eu: 'Basque',
    fa: 'Persian',
    ff: 'Fulah',
    fi: 'Finnish',
    fj: 'Fijian',
    fo: 'Faroese',
    fr: 'French',
    fy: 'Western Frisian',
    ga: 'Irish',
    gd: 'Gaelic',
    gl: 'Galician',
    gn: 'Guarani',
    gu: 'Gujarati',
    gv: 'Manx',
    ha: 'Hausa',
    he: 'Hebrew',
    hi: 'Hindi',
    ho: 'Hiri Motu',
    hr: 'Croatian',
    ht: 'Haitian Creole',
    hu: 'Hungarian',
    hy: 'Armenian',
    hz: 'Herero',
    ia: 'Interlingua (International Auxiliary Language Association)',
    id: 'Indonesian',
    ie: 'Interlingue',
    ig: 'Igbo',
    ii: 'Sichuan Yi',
    ik: 'Inupiaq',
    io: 'Ido',
    is: 'Icelandic',
    it: 'Italian',
    iu: 'Inuktitut',
    ja: 'Japanese',
    jv: 'Javanese',
    ka: 'Georgian',
    kg: 'Kongo',
    ki: 'Kikuyu',
    kj: 'Kuanyama',
    kk: 'Kazakh',
    kl: 'Kalaallisut',
    km: 'Khmer',
    kn: 'Kannada',
    ko: 'Korean',
    kr: 'Kanuri',
    ks: 'Kashmiri',
    ku: 'Kurdish',
    kv: 'Komi',
    kw: 'Cornish',
    ky: 'Kirghiz',
    la: 'Latin',
    lb: 'Luxembourgish',
    lg: 'Ganda',
    li: 'Limburgan',
    ln: 'Lingala',
    lo: 'Lao',
    lt: 'Lithuanian',
    lu: 'Luba-Katanga',
    lv: 'Latvian',
    mg: 'Malagasy',
    mh: 'Marshallese',
    mi: 'Maori',
    mk: 'Macedonian',
    ml: 'Malayalam',
    mn: 'Mongolian',
    mr: 'Marathi',
    ms: 'Malay',
    mt: 'Maltese',
    my: 'Burmese',
    na: 'Nauru',
    nb: 'Bokmål',
    nd: 'Ndebele',
    ne: 'Nepali',
    ng: 'Ndonga',
    nl: 'Dutch',
    nn: 'Norwegian Nynorsk',
    no: 'Norwegian',
    nr: 'Ndebele',
    nv: 'Navajo',
    ny: 'Chichewa',
    oc: 'Occitan (post 1500)',
    oj: 'Ojibwa',
    om: 'Oromo',
    or: 'Oriya',
    os: 'Ossetian',
    pa: 'Punjabi',
    pi: 'Pali',
    pl: 'Polish',
    ps: 'Pushto',
    pt: 'Portuguese',
    qu: 'Quechua',
    rm: 'Romansh',
    rn: 'Rundi',
    ro: 'Romanian',
    ru: 'Russian',
    rw: 'Kinyarwanda',
    sa: 'Sanskrit',
    sc: 'Sardinian',
    sd: 'Sindhi',
    se: 'Northern Sami',
    sg: 'Sango',
    si: 'Sinhala',
    sk: 'Slovak',
    sl: 'Slovenian',
    sm: 'Samoan',
    sn: 'Shona',
    so: 'Somali',
    sq: 'Albanian',
    sr: 'Serbian',
    ss: 'Swati',
    st: 'Sotho',
    su: 'Sundanese',
    sv: 'Swedish',
    sw: 'Swahili',
    ta: 'Tamil',
    te: 'Telugu',
    tg: 'Tajik',
    th: 'Thai',
    ti: 'Tigrinya',
    tk: 'Turkmen',
    tl: 'Tagalog',
    tn: 'Tswana',
    to: 'Tonga (Tonga Islands)',
    tr: 'Turkish',
    ts: 'Tsonga',
    tt: 'Tatar',
    tw: 'Twi',
    ty: 'Tahitian',
    ug: 'Uighur',
    uk: 'Ukrainian',
    ur: 'Urdu',
    uz: 'Uzbek',
    ve: 'Venda',
    vi: 'Vietnamese',
    vo: 'Volapük',
    wa: 'Walloon',
    wo: 'Wolof',
    xh: 'Xhosa',
    yi: 'Yiddish',
    yo: 'Yoruba',
    za: 'Zhuang',
    zh: 'Chinese',
    zu: 'Zulu',
} as const;

// Common language names/aliases (lowercased) not matching the canonical ISO
// name, mapped to their ISO 639-1 code.
// Mirrors _LANGUAGE_ALIAS_TO_CODE in libraries/python/language/__init__.py
const TEXT_LANGUAGE_ALIAS_TO_CODE: Record<string, string> = {
    filipino: 'tl',
    mandarin: 'zh',
    'mandarin chinese': 'zh',
    cantonese: 'zh',
    haitian: 'ht',
    'haitian creole': 'ht',
    creole: 'ht',
    'french creole': 'ht',
    farsi: 'fa',
    castilian: 'es',
    flemish: 'nl',
    panjabi: 'pa',
    burmese: 'my',
    khmer: 'km',
    cambodian: 'km',
};

const TEXT_LANGUAGE_NAME_TO_CODE: Record<string, string> = {
    ...Object.fromEntries(
        Object.entries(ISO_639_1_LANGUAGE_NAMES).map(([code, name]) => [
            name.toLowerCase(),
            code,
        ]),
    ),
    ...TEXT_LANGUAGE_ALIAS_TO_CODE,
};

/**
 * Human-readable name for any ISO 639-1 code (handling BCP 47 region tags like
 * "es-MX"), falling back to the code itself when unknown.
 * Mirrors get_language_name in libraries/python/language/__init__.py
 */
export function getLanguageName(languageCode: string): string {
    if (languageCode in ISO_639_1_LANGUAGE_NAMES) {
        return ISO_639_1_LANGUAGE_NAMES[languageCode] ?? languageCode;
    }
    const baseCode = languageCode.split('-', 1)[0];
    if (baseCode !== undefined && baseCode in ISO_639_1_LANGUAGE_NAMES) {
        return ISO_639_1_LANGUAGE_NAMES[baseCode] ?? languageCode;
    }
    return languageCode;
}

/**
 * Normalize any code or English name to an ISO 639-1 code for text (SMS).
 * Unlike normalizeSupportedLanguage (limited to the voice-speakable subset),
 * this accepts any ISO 639-1 language plus common aliases, returning null only
 * for values that do not map to a real language.
 * Mirrors normalize_text_language in libraries/python/language/__init__.py
 */
export function normalizeTextLanguage(language: string): string | null {
    const normalized = language.trim().toLowerCase();
    if (!normalized) {
        return null;
    }

    const code = normalized.split('-', 1)[0];
    if (code !== undefined && code in ISO_639_1_LANGUAGE_NAMES) {
        return code;
    }

    return TEXT_LANGUAGE_NAME_TO_CODE[normalized] ?? null;
}
